import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import './_styles_/CountryDetailTab.css';
import useExtendedState from '../../hooks/useExtendedState';
import { countryType } from '../../types';
import ApplyChangesButton from '../buttons/ApplyChangesButton';
import Privileges from '../../utils/Privileges';
import useMultiValidator from '../../hooks/useMultiValidator';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import { Privileged } from '../../hocs/withPrivileges';
import {
    COUNTRY_CODE_MAX_LENGTH,
    COUNTRY_NAME_MAX_LENGTH,
    countryCodeValidator,
    countryNameValidator,
} from '../../validators/countries';
import {
    ALL_COUNTRIES_QUERY_NAME,
    TOGGLE_COUNTRY_ACTIVATION,
    UPDATE_COUNTRY,
} from '../../graphql/countries';
import PrivilegedTextField from '../fields/privileged/PrivilegedTextField';
import ExpandedListTab from '../ExpandedListTab';
import Constants from '../../utils/Constants';
import ReadonlyField from '../fields/ReadonlyField';
import ActivationButton from '../buttons/ActivationButton';
import InfoPanel from '../InfoPanel';
import {
    transformCountryDeactivationBlockers,
    transformCountryModificationBlockers,
} from '../blockers/CountryBlockers';
import { canFieldBeModified } from '../../utils/fields';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

CountryDetailTab.propTypes = {
    country: countryType.isRequired,
};

function CountryDetailTab(props) {
    const {
        country,
    } = props;
    const { t } = useTranslation();
    const nameState = useExtendedState(country.name);
    const codeState = useExtendedState(country.code);
    const { validators, isValid } = useMultiValidator(true, {
        name: compositeValidator(countryNameValidator(t), wafValidator(t)),
        code: compositeValidator(countryCodeValidator(t), wafValidator(t)),
    });
    const isChanged = nameState.isChanged || codeState.isChanged;
    const isChangedPromise = isChanged ? Promise.resolve() : Promise.reject();

    const [isDisabled, setIsDisabled] = useState(true);
    Promise.all([isChangedPromise, isValid()])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    useDebounce(!isValid || !isChanged, Constants.VALIDITY_DELAY_IN_MS);
    const statesToResetOnSuccess = [nameState, codeState];
    const updateCountryMutation = useExtendedMutation(UPDATE_COUNTRY, {
        variables: {
            id: country.id,
            code: codeState.value,
            name: nameState.value,
        },
        statesToResetOnSuccess,
        successMessageOptions: { key: 'countries.notification.country-update-success', dataProperty: 'updateCountry' },
        failureMessageOptions: { key: 'countries.notification.country-update-failure', params: { name: country.name, code: country.code } },
        refetchQueries: [ALL_COUNTRIES_QUERY_NAME],
    });
    const isActivationButtonDisabled = country.active && country.deactivationBlockers?.length > 0;
    let toggleCountryActivationConfirmMessageKey = 'countries.confirm-activate-country';
    if (country.active) {
        toggleCountryActivationConfirmMessageKey = 'countries.confirm-deactivate-country';
    }
    const toggleCountryActivationMutation = useExtendedMutation(TOGGLE_COUNTRY_ACTIVATION, {
        variables: { id: country.id },
        statesToResetOnSuccess,
        successMessageCreator: ({ toggleCountryActivation: { active: updatedActive, name: updatedName, code: updatedCode } }) => {
            let toggleCountryActivationSuccessMessageKey = 'countries.notification.country-deactivate-success';
            if (updatedActive) {
                toggleCountryActivationSuccessMessageKey = 'countries.notification.country-activate-success';
            }
            return t(toggleCountryActivationSuccessMessageKey, { name: updatedName, code: updatedCode });
        },
        failureMessageCreator: () => {
            let toggleCountryActivationFailureMessageKey = 'countries.notification.country-activate-failure';
            if (country.active) {
                toggleCountryActivationFailureMessageKey = 'countries.notification.country-deactivate-failure';
            }
            return t(toggleCountryActivationFailureMessageKey, { name: country.name, code: country.code });
        },
        refetchQueries: [ALL_COUNTRIES_QUERY_NAME],
    });
    return (
        <ExpandedListTab
            className="country-detail-tab"
            title={t('countries.country-detail')}
        >
            <div className="country-detail-tab--readonly-fields">
                <ReadonlyField
                    label={t('countries.active')}
                    value={country.active ? t('misc.yes') : t('misc.no')}
                    className="country-detail-tab--fields--active"
                />
            </div>
            <div className="rich-list-row-expanded--fields">
                <PrivilegedTextField
                    sufficientPrivileges={canFieldBeModified('name', country.modificationBlockers) ? [Privileges.COUNTRY_EDIT] : []}
                    label={t('countries.name')}
                    state={nameState}
                    validator={validators.name}
                    className="country-detail-tab--fields--name wide"
                    placeholder={t('countries.placeholder.name')}
                    maxLength={COUNTRY_NAME_MAX_LENGTH}
                    isRequired
                />
                <PrivilegedTextField
                    sufficientPrivileges={canFieldBeModified('code', country.modificationBlockers) ? [Privileges.COUNTRY_EDIT] : []}
                    label={t('countries.code')}
                    state={codeState}
                    validator={validators.code}
                    className="country-detail-tab--fields--code"
                    placeholder={t('countries.placeholder.code')}
                    maxLength={COUNTRY_CODE_MAX_LENGTH}
                    isRequired
                />
            </div>
            <Privileged sufficientPrivileges={[Privileges.COUNTRY_EDIT]}>
                <div className="rich-list-row-expanded--buttons">
                    <div className="rich-list-row-expanded--buttons--left">
                        <ApplyChangesButton
                            extendedMutation={updateCountryMutation}
                            disabled={isDisabled}
                            confirmationMessage={t('countries.confirm-update-country', { name: country.name, code: country.code })}
                        />
                    </div>
                    <div className="rich-list-row-expanded--buttons--middle">
                        <ActivationButton
                            isActive={country.active}
                            disabled={isActivationButtonDisabled}
                            extendedMutation={toggleCountryActivationMutation}
                            confirmationMessage={t(toggleCountryActivationConfirmMessageKey, { name: country.name, code: country.code })}
                        />
                    </div>
                    <div className="rich-list-row-expanded--buttons--right" />
                </div>
                {country.deactivationBlockers?.map((deactivationBlocker) => (
                    <InfoPanel
                        key={deactivationBlocker}
                        message={transformCountryDeactivationBlockers(t, deactivationBlocker)}
                    />
                ))}
                {country?.modificationBlockers?.blockerConstants.map((modificationBlocker) => (
                    <InfoPanel
                        key={modificationBlocker}
                        message={transformCountryModificationBlockers(t, modificationBlocker)}
                    />
                ))}
            </Privileged>
        </ExpandedListTab>
    );
}

export default CountryDetailTab;
